'use client'

import { useState, useEffect, useRef } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { createPortal } from 'react-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { CustomDropdown } from '@/components/ui/custom-dropdown'
import { StoryPointInput } from '@/components/issues/story-point-input'
import { useToast } from '@/hooks/use-toast'
import { updateIssue } from '@/lib/issue-service'
import {
  X,
  Edit3,
  Link2,
  Check,
  MoreVertical,
  Target,
  FolderTree
} from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { getAuthHeaders } from '@/lib/api-utils'
import { cn } from '@/lib/utils'
import { useIssueSprintMapping } from '@/hooks/use-sprints'

interface IssueStatus {
  id: number
  name: string
  is_closed?: boolean
}

interface IssueTracker {
  id: number
  name: string
}

interface IssuePriority {
  id: number
  name: string
}

interface IssueVersion {
  id: number
  name: string
  status: string
}

interface ProjectMember {
  id: number
  name: string
  login?: string
}

interface SprintWithProject {
  id: number
  name: string
  projectId: number
  projectName?: string
  status: string
  startDate: string
  endDate: string
}

interface ContextMenuData {
  statuses: IssueStatus[]
  trackers: IssueTracker[]
  priorities: IssuePriority[]
  versions: IssueVersion[]
  members: ProjectMember[]
  sprints?: SprintWithProject[]
}

interface Issue {
  id: number
  subject: string
  description?: string
  status: {
    id: number
    name: string
    is_closed?: boolean
  }
  priority: {
    id: number
    name: string
  }
  tracker: {
    id: number
    name: string
  }
  assigned_to?: {
    id: number
    name: string
  }
  project: {
    id: number
    name: string
  }
  fixed_version?: {
    id: number
    name: string
  }
  created_on: string
  updated_on: string
  due_date?: string
}

interface IssueActionsModalProps {
  issue: Issue & { storyPoints?: number }
  isOpen: boolean
  onClose: () => void
  onActionComplete?: (updatedIssue?: any) => void
  onEditClick?: () => void
  showVersionMoveOnly?: boolean
  projectId?: number
  currentSprintId?: number
}

export function IssueActionsModal({
  issue,
  isOpen,
  onClose,
  onActionComplete,
  onEditClick,
  showVersionMoveOnly = false,
  projectId,
  currentSprintId
}: IssueActionsModalProps): JSX.Element | null {
  const locale = useLocale()
  const isRTL = locale === 'ar' || locale === 'ur'
  const t = useTranslations('issuesPage')
  const c = useTranslations('common')
  const { toast } = useToast()
  const [menuData, setMenuData] = useState<ContextMenuData | null>(null)
  const [currentIssue, setCurrentIssue] = useState<Issue>(issue)
  const [loading, setLoading] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [saving, setSaving] = useState(false)
  const [storyPoints, setStoryPoints] = useState<number | null>(issue.storyPoints ?? null)
  const [selectedSprintId, setSelectedSprintId] = useState<number | null>(
    currentSprintId !== undefined ? currentSprintId : null
  )
  const [selectedSprintProjectId, setSelectedSprintProjectId] = useState<number | null>(null)

  // Use the hook to load sprint mapping from database
  const effectiveProjectId = projectId || issue?.project?.id
  const { mapping: existingMapping, loading: loadingMapping } = useIssueSprintMapping(
    effectiveProjectId || null,
    issue?.id || null
  )

  // Use ref to track if component is still mounted during async operations
  const mountedRef = useRef(false)

  // Track if we've initialized sprint data for current issue
  const initializedIssueRef = useRef<number | null>(null)

  // Track changes to issue properties
  const [changes, setChanges] = useState<{
    status_id?: number
    tracker_id?: number
    priority_id?: number
    assigned_to_id?: number | null
    fixed_version_id?: number | null
  }>({})

  useEffect(() => {
    setMounted(true)
    mountedRef.current = true
    return () => {
      setMounted(false)
      mountedRef.current = false
    }
  }, [])

  // Single source of truth for sprint data: Load from database mapping when modal opens or issue changes
  useEffect(() => {
    // Only proceed if modal is open and we have an issue
    if (!isOpen || !issue?.id) {
      return
    }

    // If this is a different issue than we last initialized, reset everything
    if (initializedIssueRef.current !== issue.id) {
      initializedIssueRef.current = issue.id

      // Initialize with prop values if available (prevents flash)
      if (currentSprintId !== null && currentSprintId !== undefined) {
        setSelectedSprintId(currentSprintId)
      } else {
        setSelectedSprintId(null)
      }

      if (issue.storyPoints !== undefined && issue.storyPoints !== null) {
        setStoryPoints(issue.storyPoints)
      } else {
        setStoryPoints(null)
      }

      setSelectedSprintProjectId(null)
    }

    // Update from database mapping when it loads (overrides prop values)
    if (existingMapping && !loadingMapping) {
      setSelectedSprintId(existingMapping.sprintId)
      if (existingMapping.storyPoints !== null) {
        setStoryPoints(existingMapping.storyPoints)
      }
      // Note: projectId will be set when menuData.sprints loads based on sprintId
    }
  }, [isOpen, issue?.id, currentSprintId, issue.storyPoints, existingMapping, loadingMapping])

  useEffect(() => {
    if (isOpen && issue) {
      // Set loading immediately to prevent showing default text
      setLoading(true)

      // Fetch fresh issue data and menu data
      // Sprint data will be loaded by the single sprint data effect via useIssueSprintMapping hook
      void (async () => {
        const freshIssue = await fetchFreshIssueData()
        // Use the fresh issue data for menu fetch, or fallback to original issue prop
        await fetchMenuData(freshIssue || issue)
      })()
    } else if (!isOpen) {
      // Reset state when modal closes to prevent stale data
      setCurrentIssue(issue)
      setLoading(false)
      // Reset initialization tracker so next open will re-initialize
      initializedIssueRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, issue?.id])

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  // Reset changes when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setChanges({})
    }
  }, [isOpen])

  const fetchFreshIssueData = async (): Promise<Issue | null> => {
    try {
      const authHeaders = getAuthHeaders()
      if (!authHeaders) {
        throw new Error('No auth headers')
      }

      const timestamp = Date.now()
      const response = await fetch(`/api/issues/${issue.id}?_t=${timestamp}`, {
        headers: authHeaders,
        cache: 'no-store'
      })

      if (response.ok) {
        const data = await response.json()
        if (data.issue && mountedRef.current) {
          setCurrentIssue(data.issue)
          return data.issue
        }
      }
      return null
    } catch (error) {
      console.error('[IssueActionsModal] Error fetching fresh issue data:', error)
      return null
    }
  }

  const fetchMenuData = async (issueToUse: Issue): Promise<ContextMenuData> => {
    try {
      setLoading(true)
      const authHeaders = getAuthHeaders()
      if (!authHeaders) {
        throw new Error('No auth headers')
      }

      // Add cache busting timestamp to ensure fresh data
      const timestamp = Date.now()
      const issueProjectId = projectId || issueToUse.project?.id
      console.log('[IssueActionsModal] fetchMenuData - projectId prop:', projectId, 'issueToUse.project:', issueToUse.project, 'issueProjectId:', issueProjectId)

      const [allowedStatusesRes, trackersRes, prioritiesRes, versionsRes, membersRes, projectsRes] = await Promise.allSettled([
        fetch(`/api/issues/${issueToUse.id}/allowed_statuses?_t=${timestamp}`, {
          headers: authHeaders,
          cache: 'no-store'
        }),
        fetch(`/api/trackers?_t=${timestamp}`, {
          headers: authHeaders,
          cache: 'no-store'
        }),
        fetch(`/api/enumerations/issue_priorities?_t=${timestamp}`, {
          headers: authHeaders,
          cache: 'no-store'
        }),
        fetch(`/api/projects/${issueToUse.project?.id}/versions?_t=${timestamp}`, {
          headers: authHeaders,
          cache: 'no-store'
        }),
        fetch(`/api/projects/${issueToUse.project?.id}/memberships?_t=${timestamp}`, {
          headers: authHeaders,
          cache: 'no-store'
        }),
        // Fetch all projects to build parent hierarchy
        fetch(`/api/projects?limit=100&_t=${timestamp}`, {
          headers: authHeaders,
          cache: 'no-store'
        })
      ])

      const data: ContextMenuData = {
        statuses: [],
        trackers: [],
        priorities: [],
        versions: [],
        members: [],
        sprints: []
      }

      // Use allowed statuses instead of all statuses (respects workflow)
      if (allowedStatusesRes.status === 'fulfilled' && allowedStatusesRes.value.ok) {
        const statusData = await allowedStatusesRes.value.json()
        data.statuses = statusData.allowed_statuses || []

        // If no statuses, something went wrong with the API
        if (data.statuses.length === 0) {
          console.error('[IssueActionsModal] ⚠️ No statuses returned from allowed_statuses API!')
        }
      } else {
        console.error('[IssueActionsModal] Failed to fetch allowed statuses:', allowedStatusesRes.status === 'rejected' ? allowedStatusesRes.reason : 'Request failed')
      }

      if (trackersRes.status === 'fulfilled' && trackersRes.value.ok) {
        const trackerData = await trackersRes.value.json()
        data.trackers = trackerData.trackers || []
      }

      if (prioritiesRes.status === 'fulfilled' && prioritiesRes.value.ok) {
        const priorityData = await prioritiesRes.value.json()
        data.priorities = priorityData.issue_priorities || []
      }

      if (versionsRes.status === 'fulfilled' && versionsRes.value.ok) {
        const versionData = await versionsRes.value.json()
        data.versions = versionData.versions || []
      }

      if (membersRes.status === 'fulfilled' && membersRes.value.ok) {
        const memberData = await membersRes.value.json()
        const memberships = memberData.memberships || []
        data.members = memberships.map((m: any) => ({
          id: m.user?.id || m.id,
          name: m.user?.name || m.name || 'Unknown',
          login: m.user?.login || m.login
        }))
      }

      // Build parent project hierarchy and fetch sprints from all parent projects + child projects
      if (projectsRes.status === 'fulfilled' && projectsRes.value.ok && issueProjectId) {
        const projectsData = await projectsRes.value.json()
        const allProjects = projectsData.projects || []
        
        // Build map of project ID -> project
        const projectsById = new Map<number, { id: number; name: string; parent?: { id: number; name?: string } }>(
          allProjects.map((p: any) => [p.id, p])
        )
        
        // Build list of project IDs to fetch sprints from (current + up to 2 parent levels)
        const projectIdsToFetch: number[] = [issueProjectId]
        const projectNamesMap: Record<number, string> = {}
        
        // Add current project name
        const currentProject = projectsById.get(issueProjectId)
        if (currentProject) {
          projectNamesMap[issueProjectId] = currentProject.name
        }
        
        // Traverse up the parent hierarchy (max 2 levels: parent + grandparent)
        let parentProject = currentProject?.parent
        let depth = 0
        const MAX_PARENT_DEPTH = 2
        while (parentProject && parentProject.id && depth < MAX_PARENT_DEPTH) {
          projectIdsToFetch.push(parentProject.id)
          const fullParent = projectsById.get(parentProject.id)
          projectNamesMap[parentProject.id] = fullParent?.name || parentProject.name || `Project ${parentProject.id}`
          parentProject = fullParent?.parent
          depth++
        }

        // Fetch all sprints in a SINGLE request with all project IDs
        console.log('[IssueActionsModal] Fetching sprints from projects:', projectIdsToFetch)
        try {
          const sprintsRes = await fetch(`/api/db/sprints?projectIds=${projectIdsToFetch.join(',')}&_t=${timestamp}`, {
            headers: authHeaders,
            cache: 'no-store'
          })
          console.log(`[IssueActionsModal] Sprint fetch response:`, sprintsRes.status)
          if (sprintsRes.ok) {
            const sprintData = await sprintsRes.json()
            console.log(`[IssueActionsModal] Sprint data:`, sprintData)
            const sprints = sprintData.sprints || []
            // Map sprints with project names
            const allSprints: SprintWithProject[] = sprints.map((s: any) => ({
              ...s,
              projectName: projectNamesMap[s.projectId] || s.projectName || `Project ${s.projectId}`
            }))
            
            console.log('[IssueActionsModal] Total sprints fetched:', allSprints.length)
            
            // Sort sprints by start date descending
            allSprints.sort((a, b) => {
              const dateA = new Date(a.startDate).getTime()
              const dateB = new Date(b.startDate).getTime()
              return dateB - dateA
            })

            data.sprints = allSprints
          }
        } catch (err) {
          console.error(`[IssueActionsModal] Error fetching sprints:`, err)
        }
      }
      if (mountedRef.current) {
        console.log('[IssueActionsModal] Setting menuData with sprints:', data.sprints?.length)
        setMenuData(data)
      }

      return data
    } catch (error) {
      console.error('[IssueActionsModal] Error fetching menu data:', error)
      throw error
    } finally {
      if (mountedRef.current) {
        setLoading(false)
      }
    }
  }

  const handleMenuAction = (action: string) => {
    switch (action) {
      case 'edit':
        onClose()
        onEditClick?.()
        return
      case 'copy_link':
        navigator.clipboard.writeText(`${window.location.origin}/issues/${issue.id}`)
        toast({
          title: t('linkCopiedTitle'),
          description: t('linkCopiedDescription'),
        })
        onClose()
        return
      default:
        return
    }
  }

  const handleSaveChanges = async () => {
    console.log('[IssueActionsModal] handleSaveChanges called, hasChanges:', hasChanges)
    if (!hasChanges) return

    try {
      setSaving(true)

      // Build unified update payload
      const updateData: Parameters<typeof updateIssue>[1] = {}

      // Redmine issue fields
      if (Object.keys(changes).length > 0) {
        // Filter out null values for assigned_to_id (API expects undefined instead)
        const filteredChanges = { ...changes }
        if (filteredChanges.assigned_to_id === null) {
          filteredChanges.assigned_to_id = undefined
        }
        updateData.issue = filteredChanges as typeof updateData.issue
      }

      // Agile data (sprint + story points)
      console.log('[IssueActionsModal] hasStoryPointsChange:', hasStoryPointsChange, 'hasSprintChange:', hasSprintChange)
      console.log('[IssueActionsModal] selectedSprintId:', selectedSprintId, 'currentSprintId:', currentSprintId)
      console.log('[IssueActionsModal] storyPoints:', storyPoints, 'issue.storyPoints:', issue.storyPoints)
      
      if (hasStoryPointsChange || hasSprintChange) {
        updateData.agile = {}
        if (hasSprintChange) {
          updateData.agile.sprintId = selectedSprintId
        }
        if (hasStoryPointsChange) {
          updateData.agile.storyPoints = storyPoints ?? null
        }
      }

      console.log('[IssueActionsModal] Calling updateIssue with:', JSON.stringify(updateData))
      const result = await updateIssue(issue.id, updateData)
      console.log('[IssueActionsModal] updateIssue result:', result)

      if (!result.success) {
        toast({
          variant: 'destructive',
          title: t('errorGeneric'),
          description: result.error || 'Failed to update issue',
        })
        setSaving(false)
        return
      }

      toast({
        title: t('issueUpdatedTitle'),
        description: t('issueUpdatedDescription'),
      })

      // Build the updated issue object with the changes applied
      const updatedIssue = {
        ...currentIssue,
        // Apply Redmine field changes
        ...(changes.status_id && menuData ? {
          status: menuData.statuses.find(s => s.id === changes.status_id) || currentIssue.status
        } : {}),
        ...(changes.tracker_id && menuData ? {
          tracker: menuData.trackers.find(t => t.id === changes.tracker_id) || currentIssue.tracker
        } : {}),
        ...(changes.priority_id && menuData ? {
          priority: menuData.priorities.find(p => p.id === changes.priority_id) || currentIssue.priority
        } : {}),
        ...(changes.assigned_to_id !== undefined && menuData ? {
          assigned_to: changes.assigned_to_id 
            ? menuData.members.find(m => m.id === changes.assigned_to_id) || null
            : null
        } : {}),
        ...(changes.fixed_version_id !== undefined && menuData ? {
          fixed_version: changes.fixed_version_id
            ? menuData.versions.find(v => v.id === changes.fixed_version_id) || null
            : null
        } : {}),
        // Apply agile changes
        ...(hasStoryPointsChange ? { storyPoints: storyPoints } : {}),
      }

      setChanges({})
      
      // Pass the updated issue to the callback for optimistic updates
      onActionComplete?.(updatedIssue)
      onClose()
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('errorGeneric'),
        description: error instanceof Error ? error.message : t('failedToSaveChanges'),
      })
    } finally {
      setSaving(false)
    }
  }

  // Check if there are any changes (Redmine fields, story points, or sprint)
  const hasStoryPointsChange = storyPoints !== (issue.storyPoints ?? null)
  // Normalize undefined and null to null for comparison
  const normalizedSelectedSprintId = selectedSprintId ?? null
  const normalizedCurrentSprintId = currentSprintId ?? null
  const hasSprintChange = normalizedSelectedSprintId !== normalizedCurrentSprintId
  const hasChanges = Object.keys(changes).length > 0 || hasStoryPointsChange || hasSprintChange
  
  // Debug logging - remove after fixing
  console.log('[IssueActionsModal] Change detection:', {
    selectedSprintId,
    currentSprintId,
    normalizedSelectedSprintId,
    normalizedCurrentSprintId,
    hasSprintChange,
    storyPoints,
    issueStoryPoints: issue.storyPoints,
    hasStoryPointsChange,
    changesKeys: Object.keys(changes),
    hasChanges
  })

  // Get current values (with changes applied)
  const getCurrentValue = (field: keyof typeof changes) => {
    if (changes[field] !== undefined) {
      return changes[field]
    }
    switch (field) {
      case 'status_id':
        return currentIssue.status?.id
      case 'tracker_id':
        return currentIssue.tracker?.id
      case 'priority_id':
        return currentIssue.priority?.id
      case 'assigned_to_id':
        return currentIssue.assigned_to?.id || null
      case 'fixed_version_id':
        return currentIssue.fixed_version?.id || null
      default:
        return null
    }
  }

  if (!mounted || !isOpen || !issue) return null

  const sidebarContent = (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Sidebar */}
      <Card className={cn(
        "absolute top-0 bottom-0 h-full w-full max-w-2xl rounded-none shadow-2xl animate-in",
        isRTL ? "left-0 border-r slide-in-from-left" : "right-0 border-l slide-in-from-right"
      )}>
        <CardContent className="flex h-full flex-col p-0">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <MoreVertical className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">Issue Actions</h2>
                <p className="text-sm text-muted-foreground">
                  #{issue.id}: {issue.subject}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-background">
            <div className="space-y-6 p-6">
              {(loading && !menuData) || !currentIssue.status ? (
                <div className="space-y-4">
                  {/* Skeleton Loading */}
                      <div className="flex items-center gap-2 pb-2 border-b">
                        <div className="h-1 w-1 rounded-full bg-primary"></div>
                        <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                          {t('quickUpdate')}
                        </h3>
                      </div>

                  {/* Status Skeleton */}
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-11 w-full rounded-md" />
                  </div>

                  {/* Tracker Skeleton */}
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-11 w-full rounded-md" />
                  </div>

                  {/* Priority Skeleton */}
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-11 w-full rounded-md" />
                  </div>

                  {/* Assignee Skeleton */}
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-11 w-full rounded-md" />
                  </div>
                </div>
              ) : (
                <>
                  {/* Version Move Section (only shown when showVersionMoveOnly is true) */}
                  {showVersionMoveOnly && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 pb-2 border-b">
                        <div className="h-1 w-1 rounded-full bg-primary"></div>
                        <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                          {t('moveToVersion')}
                        </h3>
                      </div>

                      {/* Version Dropdown */}
                      {menuData && menuData.versions.length > 0 && (
                        <CustomDropdown
                          label={t('fields.version')}
                          currentValue={
                            getCurrentValue('fixed_version_id')
                              ? menuData.versions.find(v => v.id === getCurrentValue('fixed_version_id'))?.name || c('noVersion')
                              : currentIssue.fixed_version?.name || c('noVersion')
                          }
                          options={[
                            { id: null, name: c('noVersion'), isSelected: getCurrentValue('fixed_version_id') === null },
                            ...menuData.versions
                              .filter(v => v.status === 'open' || v.status === 'locked')
                              .map((version) => ({
                                id: version.id,
                                name: version.name,
                                isSelected: getCurrentValue('fixed_version_id') === version.id
                              }))
                          ]}
                          onSelect={(id) => {
                            const currentVersionId = currentIssue.fixed_version?.id || null
                            if (id !== currentVersionId) {
                              setChanges(prev => ({ ...prev, fixed_version_id: id as number | null }))
                            } else if (id === currentVersionId && changes.fixed_version_id !== undefined) {
                              const { fixed_version_id, ...rest } = changes
                              setChanges(rest)
                            }
                          }}
                        />
                      )}
                      
                      {(!menuData || menuData.versions.length === 0) && (
                        <div className="space-y-1">
                          <label className="text-sm font-medium text-muted-foreground">{t('fields.version') || 'Target Version'}</label>
                          <div className="text-sm text-muted-foreground italic px-3 py-2 bg-muted/50 rounded-md">
                            No versions available for this project
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Issue Properties (hidden when showVersionMoveOnly is true) */}
                  {!showVersionMoveOnly && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b">
                      <div className="h-1 w-1 rounded-full bg-primary"></div>
                      <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                        Quick Update
                      </h3>
                    </div>

                    {/* Status Dropdown */}
                    {menuData && menuData.statuses.length > 0 ? (
                      <CustomDropdown
                        label={t('fields.status')}
                        currentValue={
                          menuData.statuses.find(s => s.id === getCurrentValue('status_id'))?.name || currentIssue.status?.name || ''
                        }
                        options={menuData.statuses.map((status) => ({
                          id: status.id,
                          name: status.name,
                          isSelected: getCurrentValue('status_id') === status.id
                        }))}
                        onSelect={(id) => {
                          if (id && id !== currentIssue.status?.id) {
                            setChanges(prev => ({ ...prev, status_id: id as number }))
                          } else if (id === currentIssue.status?.id && changes.status_id !== undefined) {
                            const { status_id, ...rest } = changes
                            setChanges(rest)
                          }
                        }}
                      />
                    ) : (
                      <div className="space-y-1">
                        <label className="text-sm font-medium text-muted-foreground">{t('fields.status')}</label>
                        <div className="text-sm text-muted-foreground italic px-3 py-2 bg-muted/50 rounded-md">
                          {t('statusNoTransitions', { status: currentIssue.status?.name || 'Unknown' })}
                        </div>
                      </div>
                    )}

                    {/* Tracker Dropdown */}
                    {menuData && menuData.trackers.length > 0 && (
                      <CustomDropdown
                        label={t('fields.tracker')}
                        currentValue={
                          menuData.trackers.find(t => t.id === getCurrentValue('tracker_id'))?.name || currentIssue.tracker?.name || ''
                        }
                        options={menuData.trackers.map((tracker) => ({
                          id: tracker.id,
                          name: tracker.name,
                          isSelected: getCurrentValue('tracker_id') === tracker.id
                        }))}
                        onSelect={(id) => {
                          if (id && id !== currentIssue.tracker?.id) {
                            setChanges(prev => ({ ...prev, tracker_id: id as number }))
                          } else if (id === currentIssue.tracker?.id && changes.tracker_id !== undefined) {
                            const { tracker_id, ...rest } = changes
                            setChanges(rest)
                          }
                        }}
                      />
                    )}

                    {/* Priority Dropdown */}
                    {menuData && menuData.priorities.length > 0 && (
                      <CustomDropdown
                        label={t('fields.priority')}
                        currentValue={
                          menuData.priorities.find(p => p.id === getCurrentValue('priority_id'))?.name || currentIssue.priority?.name || ''
                        }
                        options={menuData.priorities.map((priority) => ({
                          id: priority.id,
                          name: priority.name,
                          isSelected: getCurrentValue('priority_id') === priority.id
                        }))}
                        onSelect={(id) => {
                          if (id && id !== currentIssue.priority?.id) {
                            setChanges(prev => ({ ...prev, priority_id: id as number }))
                          } else if (id === currentIssue.priority?.id && changes.priority_id !== undefined) {
                            const { priority_id, ...rest } = changes
                            setChanges(rest)
                          }
                        }}
                      />
                    )}

                    {/* Assignee Dropdown */}
                    {menuData && (
                      <CustomDropdown
                        label={t('fields.assignee')}
                        currentValue={
                          getCurrentValue('assigned_to_id')
                            ? menuData.members.find(m => m.id === getCurrentValue('assigned_to_id'))?.name || '(nobody)'
                            : t('unassigned')
                        }
                        options={[
                          { id: null, name: t('unassigned'), isSelected: getCurrentValue('assigned_to_id') === null },
                          ...menuData.members.map((member) => ({
                            id: member.id,
                            name: member.name,
                            isSelected: getCurrentValue('assigned_to_id') === member.id
                          }))
                        ]}
                        onSelect={(id) => {
                          const currentAssigneeId = currentIssue.assigned_to?.id || null
                          if (id !== currentAssigneeId) {
                            setChanges(prev => ({ ...prev, assigned_to_id: id as number | null }))
                          } else if (id === currentAssigneeId && changes.assigned_to_id !== undefined) {
                            const { assigned_to_id, ...rest } = changes
                            setChanges(rest)
                          }
                        }}
                      />
                    )}

                    {/* Story Points Dropdown */}
                    <StoryPointInput
                      value={storyPoints}
                      onChange={setStoryPoints}
                      fibonacci={true}
                      showLabel={true}
                      label="Story Points"
                    />

                    {/* Sprint Dropdown */}
                    {loading && !menuData ? (
                      <div className="space-y-2">
                        <div className="h-4 w-20 bg-muted rounded animate-pulse" />
                        <div className="h-10 w-full bg-muted rounded animate-pulse" />
                      </div>
                    ) : menuData && menuData.sprints && menuData.sprints.length > 0 ? (
                      (() => {
                        // Create indexed sprint options for dropdown (which requires number IDs)
                        const sprintOptions = menuData.sprints.map((sprint, index) => {
                          const issueProjectId = projectId || issue.project?.id
                          const isFromParent = sprint.projectId !== issueProjectId
                          return {
                            index,
                            sprint,
                            displayName: isFromParent
                              ? `${sprint.name} (${sprint.projectName})`
                              : sprint.name
                          }
                        })

                        const selectedIndex = selectedSprintId !== null && selectedSprintId !== undefined
                          ? sprintOptions.findIndex(o => o.sprint.id === selectedSprintId)
                          : -1

                        return (
                          <CustomDropdown
                            label="Sprint"
                            currentValue={
                              selectedIndex >= 0 
                                ? sprintOptions[selectedIndex].displayName 
                                : (selectedSprintId !== null && selectedSprintId !== undefined)
                                  ? 'Sprint (Loading...)' // Sprint ID set but not found in list yet
                                  : 'No Sprint'
                            }
                            options={[
                              { id: null, name: 'No Sprint', isSelected: selectedSprintId === null },
                              ...sprintOptions.map(opt => ({
                                id: opt.index,
                                name: opt.displayName,
                                isSelected: selectedIndex === opt.index
                              }))
                            ]}
                            onSelect={(indexOrNull) => {
                              if (indexOrNull === null) {
                                setSelectedSprintId(null)
                                setSelectedSprintProjectId(null)
                              } else {
                                const selected = sprintOptions[indexOrNull as number]
                                if (selected) {
                                  setSelectedSprintId(selected.sprint.id)
                                  setSelectedSprintProjectId(selected.sprint.projectId)
                                }
                              }
                            }}
                          />
                        )
                      })()
                    ) : (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-muted-foreground">Sprint</label>
                        <div className="text-sm text-muted-foreground italic px-3 py-2 bg-muted/50 rounded-md">
                          No sprints available
                        </div>
                      </div>
                    )}
                  </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Footer with Actions */}
          <div className="border-t bg-muted/30">
            {/* Save Changes Button (if there are changes) */}
            {hasChanges && (
              <div className="px-6 pt-5">
                <Button
                  onClick={handleSaveChanges}
                  disabled={saving}
                  className="w-full shadow-sm"
                  size="lg"
                >
                  {saving ? (
                    <>
                      <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                      <span className={isRTL ? 'mr-2' : 'ml-2'}>{t('savingChanges')}</span>
                    </>
                  ) : (
                    <>
                      <Check className={cn('w-4 h-4', isRTL ? 'ml-2' : 'mr-2')} />
                      <span>{t('saveChanges')}</span>
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Edit Button (hidden when showVersionMoveOnly is true) */}
            {!showVersionMoveOnly && (
            <div className={cn("px-6", hasChanges ? "py-4" : "py-5")}>
              <Button
                onClick={() => handleMenuAction('edit')}
                variant="outline"
                size="lg"
                className="w-full border-2 hover:bg-primary/5 hover:border-primary/50 hover:shadow-md transition-all duration-200"
              >
                <Edit3 className={cn('w-4 h-4', isRTL ? 'ml-2' : 'mr-2')} />
                <span className="font-medium">{c('edit')}</span>
              </Button>
            </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )

  return createPortal(sidebarContent, document.body)
}
